import { useEffect, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Terminal } from '@xterm/xterm'
import { FitAddon } from '@xterm/addon-fit'
import '@xterm/xterm/css/xterm.css'
import { operatorApi, type Tenant, type EdgeNode, type ConfigTier, type DeploymentLogEntry, type EdgeFile } from '../../api/operator'
import { useAdminToken } from './AdminLayout'
import { EDGE_SCAN_COMMANDS, EDGE_SCAN_OPTIONS, EDGE_SCAN_NOTE } from './edgeCommands'
import { Badge, type BadgeTone } from '../../components/Badge'

// I-SOFT-staff-only deploy-tier tooling: config_tier indicator, shell-terminal
// browser widget, deployment log viewer. Lives in its own file (isoft-admin
// owned) rather than inside AdminTenantScada.tsx (scada owned) — wired in from
// AdminTenantDetail.tsx instead of touching scada's file.
//
// The shell terminal and log/file panes below keep a dark, monospace
// treatment deliberately — a terminal/console reading dark-on-light-chrome
// is a standard convention (same as this repo's own DevicesModal diagnostic
// output elsewhere), not a leftover of the purple internal-tool identity
// this file's surrounding chrome is being moved off of.

const TIER_LABEL: Record<ConfigTier, string> = {
  base: 'Base',
  extended: 'Extended',
  deploy: 'Deploy',
}

// 'deploy' unlocks the shell terminal -- marked critical (red) on purpose,
// not just carried over from its old purple: it's the genuinely dangerous
// tier, and the existing Badge palette already has a tone for that.
const TIER_TONE: Record<ConfigTier, BadgeTone> = {
  base: 'neutral',
  extended: 'info',
  deploy: 'critical',
}

const TIER_ORDER: ConfigTier[] = ['base', 'extended', 'deploy']

function TierControl({ tenant, node }: { tenant: Tenant; node: EdgeNode }) {
  const token = useAdminToken()
  const qc = useQueryClient()
  const [err, setErr] = useState('')
  const [confirmDeploy, setConfirmDeploy] = useState(false)

  const mut = useMutation({
    mutationFn: (config_tier: ConfigTier) => operatorApi.updateEdgeNode(token, tenant.id, node.id, { config_tier }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['op-edge-nodes', tenant.id, token] })
      setErr('')
      setConfirmDeploy(false)
    },
    onError: (e: unknown) =>
      setErr((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Greška pri promeni tier-a.'),
  })

  const handleClick = (tier: ConfigTier) => {
    if (tier === node.config_tier || mut.isPending) return
    if (tier === 'deploy') { setErr(''); setConfirmDeploy(true); return }
    mut.mutate(tier)
  }

  return (
    <div>
      <div className="flex items-center gap-1">
        {TIER_ORDER.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => handleClick(t)}
            disabled={mut.isPending}
            title={`Postaviti na ${TIER_LABEL[t]}`}
            className={`rounded-full transition-opacity disabled:opacity-50 ${
              node.config_tier === t ? '' : 'opacity-40 hover:opacity-70'
            }`}
          >
            <Badge tone={TIER_TONE[t]}>{TIER_LABEL[t]}</Badge>
          </button>
        ))}
      </div>
      {err && <p className="text-xs text-red-600 mt-1">{err}</p>}

      {confirmDeploy && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" onClick={() => setConfirmDeploy(false)} />
          <div className="relative bg-white border border-red-200 rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
            <h2 className="text-base font-semibold text-gray-900">Postaviti na Deploy tier?</h2>
            <p className="text-sm text-gray-500">
              Čvor <span className="text-gray-900 font-semibold">{node.name}</span> dobija pristup terminalu i slanju
              sirovih logova — namenjeno isključivo internoj dijagnostici I-SOFT osoblja. Promena važi tek nakon
              ponovnog preuzimanja paketa i pokretanja{' '}
              <code className="text-xs bg-gray-100 px-1 rounded-sm">setup.sh</code> na čvoru.
            </p>
            {err && <p className="text-xs text-red-600">{err}</p>}
            <div className="flex gap-2">
              <button
                onClick={() => mut.mutate('deploy')}
                disabled={mut.isPending}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold py-2.5 rounded-lg disabled:opacity-50"
              >
                {mut.isPending ? 'Postavljanje…' : 'Postaviti na Deploy'}
              </button>
              <button onClick={() => setConfirmDeploy(false)}
                className="flex-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium py-2.5 rounded-lg">
                Otkazati
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const SHELL_CLOSE_MESSAGES: Record<number, string> = {
  4401: 'Tiket je istekao, već iskorišćen, ili ne odgovara ovom zakupcu — pokušajte ponovo.',
  4404: 'Edge čvor trenutno nije povezan sa cloud-om — pokušajte ponovo za koji trenutak.',
  4409: 'Neko drugi već ima otvorenu terminal sesiju na ovom čvoru.',
}

function ShellCommandsPanel({ onPaste }: { onPaste: (command: string) => void }) {
  const [copiedCommand, setCopiedCommand] = useState('')

  const copy = (command: string) => {
    navigator.clipboard.writeText(command).then(() => {
      setCopiedCommand(command)
      setTimeout(() => setCopiedCommand(''), 1500)
    })
  }

  return (
    <div className="w-64 shrink-0 border border-gray-200 rounded-lg bg-gray-50 overflow-y-auto" style={{ height: '420px' }}>
      <div className="p-3 space-y-2">
        {EDGE_SCAN_COMMANDS.map((c) => (
          <div key={c.command} className="space-y-1">
            <p className="text-xs text-gray-500">{c.label}</p>
            <div className="flex items-stretch gap-1">
              <code className="flex-1 text-xs bg-white border border-gray-200 text-gray-700 px-1.5 py-1 rounded-sm break-all">{c.command}</code>
            </div>
            <div className="flex gap-1">
              <button
                onClick={() => onPaste(c.command)}
                className="flex-1 text-xs font-medium text-brand-cyan-text hover:text-brand-midnight hover:bg-blue-50 px-1.5 py-1 rounded-md transition-colors"
              >
                Nalepiti u terminal
              </button>
              <button
                onClick={() => copy(c.command)}
                className="text-xs font-medium text-gray-500 hover:text-gray-900 hover:bg-gray-200 px-1.5 py-1 rounded-md transition-colors"
              >
                {copiedCommand === c.command ? 'Kopirano' : 'Kopirati'}
              </button>
            </div>
          </div>
        ))}
        <div className="pt-2 mt-2 border-t border-gray-200 space-y-1">
          {EDGE_SCAN_OPTIONS.map((o) => (
            <p key={o.flag} className="text-xs text-gray-500">
              <code className="text-gray-600">{o.flag}</code>: {o.description}
            </p>
          ))}
        </div>
        <p className="text-xs text-gray-400 pt-1">{EDGE_SCAN_NOTE}</p>
      </div>
    </div>
  )
}

function ShellModal({ tenant, node, onClose }: { tenant: Tenant; node: EdgeNode; onClose: () => void }) {
  const token = useAdminToken()
  const containerRef = useRef<HTMLDivElement>(null)
  const [status, setStatus] = useState<'connecting' | 'open' | 'closed'>('connecting')
  const [closeMessage, setCloseMessage] = useState('')
  const [showCommands, setShowCommands] = useState(false)
  const wsRef = useRef<WebSocket | null>(null)
  const termRef = useRef<Terminal | null>(null)
  const fitRef = useRef<FitAddon | null>(null)

  useEffect(() => {
    let cancelled = false
    let ws: WebSocket | null = null
    const term = new Terminal({
      convertEol: true,
      fontSize: 13,
      theme: { background: '#111827', foreground: '#e5e7eb' },
    })
    termRef.current = term
    const fit = new FitAddon()
    fitRef.current = fit
    term.loadAddon(fit)
    if (containerRef.current) {
      term.open(containerRef.current)
      fit.fit()
    }
    const handleResize = () => fit.fit()
    window.addEventListener('resize', handleResize)

    operatorApi.createShellTicket(token, tenant.id, node.id)
      .then((r) => {
        if (cancelled) return
        const socket = new WebSocket(operatorApi.shellWebSocketUrl(tenant.id, node.id, r.data.ticket))
        ws = socket
        wsRef.current = socket
        socket.binaryType = 'arraybuffer'
        socket.onopen = () => setStatus('open')
        socket.onmessage = (ev) => {
          if (typeof ev.data === 'string') {
            term.write(ev.data)
          } else {
            term.write(new Uint8Array(ev.data as ArrayBuffer))
          }
        }
        socket.onclose = (ev) => {
          setStatus('closed')
          setCloseMessage(SHELL_CLOSE_MESSAGES[ev.code] ?? (ev.reason || 'Veza sa terminalom je prekinuta.'))
        }
        term.onData((data) => {
          // Must be a binary frame — the edge relay treats text frames as
          // JSON control messages (start_session/resize/end_session) and
          // silently discards anything that isn't valid JSON, keystrokes
          // included. socket.send(string) always sends text, so encode.
          if (socket.readyState === WebSocket.OPEN) socket.send(new TextEncoder().encode(data))
        })
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setStatus('closed')
        setCloseMessage(
          (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Nije moguće otvoriti terminal sesiju.',
        )
      })

    return () => {
      cancelled = true
      window.removeEventListener('resize', handleResize)
      ws?.close()
      wsRef.current = null
      termRef.current = null
      term.dispose()
    }
  }, [token, tenant.id, node.id])

  useEffect(() => {
    // Panel width change resizes the terminal container -- refit after layout settles.
    const id = requestAnimationFrame(() => fitRef.current?.fit())
    return () => cancelAnimationFrame(id)
  }, [showCommands])

  const pasteToTerminal = (command: string) => {
    const socket = wsRef.current
    if (socket?.readyState === WebSocket.OPEN) socket.send(new TextEncoder().encode(command))
    termRef.current?.focus()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className={`relative bg-white rounded-2xl shadow-2xl w-full p-4 space-y-3 transition-[max-width] ${showCommands ? 'max-w-4xl' : 'max-w-3xl'}`}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Terminal — {node.name}</h2>
            <p className="text-xs text-gray-500">Vidi samo edge kontejner, ne ceo host.</p>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setShowCommands((v) => !v)}
              className={`text-xs font-medium px-2 py-1 rounded-lg transition-colors ${
                showCommands ? 'text-brand-cyan-text bg-blue-50' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              Komande
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700 px-2">✕</button>
          </div>
        </div>
        {status === 'connecting' && <p className="text-xs text-gray-500">Povezivanje…</p>}
        {status === 'closed' && closeMessage && <p className="text-xs text-amber-600">{closeMessage}</p>}
        <div className="flex gap-3">
          {/* Terminal pane stays dark deliberately -- see file-level note. */}
          <div ref={containerRef} className="flex-1 rounded-lg overflow-hidden border border-gray-800 bg-[#111827]" style={{ height: '420px' }} />
          {showCommands && <ShellCommandsPanel onPaste={pasteToTerminal} />}
        </div>
      </div>
    </div>
  )
}

// Log pane itself stays dark deliberately -- see file-level note.
const LOG_LEVEL_CLS: Record<string, string> = {
  ERROR: 'text-red-400',
  CRITICAL: 'text-red-400',
  WARNING: 'text-amber-400',
  INFO: 'text-gray-300',
  DEBUG: 'text-gray-500',
}

const LOGS_PAGE_SIZE = 200

function LogsModal({ tenant, node, onClose }: { tenant: Tenant; node: EdgeNode; onClose: () => void }) {
  const token = useAdminToken()
  const [entries, setEntries] = useState<DeploymentLogEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [hasMore, setHasMore] = useState(true)

  const load = (before?: string) => {
    setLoading(true)
    operatorApi.listDeploymentLogs(token, tenant.id, node.id, { limit: LOGS_PAGE_SIZE, before })
      .then((r) => {
        setEntries((prev) => (before ? [...prev, ...r.data] : r.data))
        setHasMore(r.data.length === LOGS_PAGE_SIZE)
        setErr('')
      })
      .catch((e: unknown) =>
        setErr((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Greška pri učitavanju logova.'),
      )
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant.id, node.id, token])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900">Deploy logovi — {node.name}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 px-2">✕</button>
        </div>
        {err && <p className="text-xs text-red-600">{err}</p>}
        {/* Log pane stays dark/monospace deliberately -- see file-level note.
            Solid, not translucent, now that the modal chrome around it is
            light -- bg-black/30 read fine against the old dark shell but
            would wash out to pale gray against a white one. */}
        <div className="flex-1 overflow-y-auto rounded-lg border border-gray-800 bg-gray-900 font-mono text-xs">
          {entries.length === 0 && !loading ? (
            <div className="py-8 text-center text-gray-500">Nema logova.</div>
          ) : (
            entries.map((e) => (
              <div key={e.id} className="px-3 py-1.5 border-b border-gray-800/60 flex gap-2">
                <span className="text-gray-600 shrink-0">{new Date(e.captured_at).toLocaleString('sr-Latn-RS')}</span>
                <span className={`shrink-0 font-semibold ${LOG_LEVEL_CLS[e.level] ?? 'text-gray-300'}`}>{e.level}</span>
                {e.device_key && <span className="text-gray-500 shrink-0">[{e.device_key}]</span>}
                <span className="text-gray-300 break-all">{e.message}</span>
              </div>
            ))
          )}
        </div>
        <div className="flex justify-center">
          {hasMore && entries.length > 0 && (
            <button
              onClick={() => load(entries[entries.length - 1].captured_at)}
              disabled={loading}
              className="text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
            >
              {loading ? 'Učitavanje…' : 'Učitati starije'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

function FilesModal({ tenant, node, onClose }: { tenant: Tenant; node: EdgeNode; onClose: () => void }) {
  const token = useAdminToken()
  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  const [err, setErr] = useState('')

  const { data: files = [], isLoading } = useQuery({
    queryKey: ['op-edge-files', tenant.id, node.id, token],
    queryFn: () => operatorApi.listEdgeFiles(token, tenant.id, node.id).then((r) => r.data),
    enabled: !!tenant.id && !!node.id && !!token,
  })

  const handleDownload = (file: EdgeFile) => {
    setErr('')
    setDownloadingId(file.id)
    operatorApi.downloadEdgeFile(token, tenant.id, node.id, file.id)
      .then((r) => {
        const url = URL.createObjectURL(r.data)
        const a = document.createElement('a')
        a.href = url
        a.download = file.filename
        a.click()
        URL.revokeObjectURL(url)
      })
      .catch((e: unknown) =>
        setErr((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Greška pri preuzimanju fajla.'),
      )
      .finally(() => setDownloadingId(null))
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900">Izveštaji — {node.name}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 px-2">✕</button>
        </div>
        {err && <p className="text-xs text-red-600">{err}</p>}
        <div className="flex-1 overflow-y-auto rounded-lg border border-gray-200">
          {files.length === 0 ? (
            <div className="py-8 text-center text-gray-500 text-sm space-y-1">
              <p>{isLoading ? 'Učitavanje…' : 'Nema izveštaja.'}</p>
              {!isLoading && (
                <p className="text-xs text-gray-400">Pokrenite sken u terminalu (deploy tier) da biste generisali izveštaj.</p>
              )}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Fajl', 'Veličina', 'Primljen', 'Ističe', ''].map((h) => (
                    <th key={h} className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {files.map((f) => (
                  <tr key={f.id} className="border-t border-gray-100">
                    <td className="px-4 py-2 text-gray-700 font-mono text-xs break-all">{f.filename}</td>
                    <td className="px-4 py-2 text-gray-500 whitespace-nowrap">{formatBytes(f.size_bytes)}</td>
                    <td className="px-4 py-2 text-gray-500 whitespace-nowrap">{new Date(f.received_at).toLocaleString('sr-Latn-RS')}</td>
                    <td className="px-4 py-2 text-gray-500 whitespace-nowrap">{new Date(f.expires_at).toLocaleDateString('sr-Latn-RS')}</td>
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={() => handleDownload(f)}
                        disabled={downloadingId === f.id}
                        className="text-xs font-medium text-brand-cyan-text hover:text-brand-midnight hover:bg-blue-50 disabled:opacity-50 px-2 py-1 rounded-lg transition-colors"
                      >
                        {downloadingId === f.id ? 'Preuzimanje…' : 'Preuzeti'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <p className="text-xs text-gray-400">
          Izveštaji se automatski brišu 7 dana nakon prijema. Komande: vidi Terminal → Komande.
        </p>
      </div>
    </div>
  )
}

export default function AdminEdgeDeployTools({ tenant }: { tenant: Tenant }) {
  const token = useAdminToken()
  const { data: nodes = [] } = useQuery({
    queryKey: ['op-edge-nodes', tenant.id, token],
    queryFn: () => operatorApi.listEdgeNodes(token, tenant.id).then((r) => r.data),
    enabled: !!tenant.id && !!token,
    staleTime: 30_000,
  })

  const [shellNode, setShellNode] = useState<EdgeNode | null>(null)
  const [logsNode, setLogsNode] = useState<EdgeNode | null>(null)
  const [filesNode, setFilesNode] = useState<EdgeNode | null>(null)

  if (nodes.length === 0) return null

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-sm font-semibold text-gray-900">Deploy alatke</h2>
        <p className="text-xs text-gray-500 mt-0.5">
          Samo za I-SOFT osoblje. Terminal i logovi pokazuju samo ono što vidi edge proces, ne ceo host. Promena
          tier-a važi tek nakon ponovnog preuzimanja paketa i pokretanja setup.sh na čvoru.
        </p>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            {['Čvor', 'Tier', 'Akcije'].map((h) => (
              <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {nodes.map((node) => (
            <tr key={node.id} className="border-t border-gray-100 hover:bg-gray-50">
              <td className="px-5 py-3 text-gray-900 font-medium">{node.name}</td>
              <td className="px-5 py-3"><TierControl tenant={tenant} node={node} /></td>
              <td className="px-5 py-3 whitespace-nowrap">
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setLogsNode(node)}
                    className="text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 px-2 py-1.5 rounded-lg transition-colors"
                  >
                    Logovi
                  </button>
                  <button
                    onClick={() => setFilesNode(node)}
                    className="text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 px-2 py-1.5 rounded-lg transition-colors"
                  >
                    Izveštaji
                  </button>
                  <button
                    onClick={() => setShellNode(node)}
                    disabled={node.config_tier !== 'deploy'}
                    title={node.config_tier !== 'deploy' ? 'Terminal je dostupan samo za deploy tier' : undefined}
                    className="text-xs font-medium text-brand-cyan-text hover:text-brand-midnight hover:bg-blue-50 disabled:opacity-30 disabled:hover:bg-transparent px-2 py-1.5 rounded-lg transition-colors"
                  >
                    Terminal
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {shellNode && <ShellModal tenant={tenant} node={shellNode} onClose={() => setShellNode(null)} />}
      {logsNode && <LogsModal tenant={tenant} node={logsNode} onClose={() => setLogsNode(null)} />}
      {filesNode && <FilesModal tenant={tenant} node={filesNode} onClose={() => setFilesNode(null)} />}
    </div>
  )
}
